import React from "react";
import {
    Backdrop,
    Box,
    Button,
    Stack,
    Typography,
    useTheme,
    alpha,
} from "@mui/material";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { FormattedMessage } from "react-intl";
import { tokens } from "../../../assets/theme";
import CountdownTimer from "./countdownTimer";
import messages from "./messages";

export default function MachineInSetupOverlay(props) {
    const {
        machineQuery,
        machineName,
        onClose,
        allowDismiss = false,
    } = props;

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const setupUntil = machineQuery?.data?.machineInSetupUntil;
    const isLate = setupUntil ? new Date(setupUntil) <= new Date() : false;

    const accentColor = isLate ? colors.redAccent[600] : "#1565C0";
    const accentSoft = isLate
        ? alpha(colors.redAccent[600], 0.18)
        : alpha("#1565C0", 0.18);

    return (
        <Backdrop
            open={true}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 20,
                backgroundColor: "rgba(5, 10, 20, 0.72)",
                backdropFilter: "blur(3px)",
            }}
        >
            <Box
                sx={{
                    width: "min(1000px, calc(100vw - 32px))",
                    borderRadius: "20px",
                    overflow: "hidden",
                    backgroundColor: colors.primary[400],
                    boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
                    border: `1px solid ${alpha("#ffffff", 0.08)}`,
                }}
            >
                <Box
                    sx={{
                        backgroundColor: accentColor,
                        px: 3,
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    {isLate ? (
                        <WarningAmberRoundedIcon sx={{ fontSize: 50 }} />
                    ) : (
                        <BuildCircleOutlinedIcon sx={{ fontSize: 50 }} />
                    )}

                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            <FormattedMessage {...messages.machineInSetupOverlayTitle} />
                        </Typography>

                        {!!machineName && (
                            <Typography variant="body2" sx={{ opacity: 0.92, fontSize: "1.5rem"}}>
                                {machineName}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{ px: 4, py: 4 }}>
                    <Stack spacing={3} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: "100%",
                                borderRadius: "16px",
                                backgroundColor: accentSoft,
                                border: `1px solid ${alpha(accentColor, 0.35)}`,
                                py: 3,
                                px: 2,
                            }}
                        >
                            <Stack spacing={1} alignItems="center">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <AccessTimeOutlinedIcon sx={{ opacity: 0.9 }} />
                                    <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                        {isLate ? (
                                            <FormattedMessage {...messages.machineInSetupLate} />
                                        ) : (
                                            <FormattedMessage {...messages.machineInSetupEndsIn} />
                                        )}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: "-0.03em",
                                        lineHeight: 1,
                                        fontSize: {
                                            xs: "5rem",
                                            sm: "7rem",
                                            md: "10rem",
                                        },
                                    }}
                                >
                                    <CountdownTimer
                                        maxClosedDate={setupUntil}
                                        showSeconds={false}
                                        showCountUp={true}
                                    />
                                </Typography>
                            </Stack>
                        </Box>

                        <Stack spacing={1}>
                            <Typography
                                variant="body1"
                                sx={{
                                    maxWidth: 760,
                                    color: alpha("#fff", 0.8),
                                    fontSize: "1.0rem",
                                }}
                            >
                                <FormattedMessage {...messages.machineInSetupOverlayDescription} />
                            </Typography>
                        </Stack>

                        {allowDismiss && (
                            <Button
                                variant="contained"
                                onClick={onClose}
                                sx={{
                                    mt: 1,
                                    minWidth: 180,
                                    fontSize: "1.1rem",
                                    backgroundColor: accentColor,
                                    "&:hover": {
                                        backgroundColor: accentColor,
                                        filter: "brightness(1.06)",
                                    },
                                }}
                            >
                                <FormattedMessage {...messages.close} />
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Backdrop>
    );
}